import CalculateIcon from "@mui/icons-material/Calculate";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, Chip, Paper, Stack, Typography, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, type ReactElement, type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { FONT_SIZE } from "../../../theme/tokens";
import type { PbjPrediction } from "../../../utils/models";
import { formatCurrency } from "../../../utils/utilFunctions";
import { CensusDialog } from "./CensusDialog";
import { resolveCasperCmi, type SavedCmi } from "./casperCmi.utils";
import { floatShadow, heroGhostBorder, heroGhostFill, heroGhostHover, heroGlassPill, heroInk, heroInkMuted, heroLine, heroSurface, PBJ_RADIUS } from "./colors";
import { FacilityInputsDialog } from "./hero/FacilityInputsDialog";
import { HeroGapStory } from "./hero/HeroGapStory";
import { HeroProgressMeter } from "./hero/HeroProgressMeter";
import { HeroScoreLead } from "./hero/HeroScoreLead";
import type { PbjDraftControls } from "./pbjDraftControls";
import type { PlanOutcome } from "./planOutcome.utils";
import { MIN_PLAN_LIFT_POINTS, PREDICTION_MULTICOL_QUERY } from "./predictionLayout.consts";
import { resolveTargetPoints } from "./resolveTargetPoints.utils";
import { SetTargetDialog } from "./SetTargetDialog";

type Props = {
	prediction: PbjPrediction;
	// Live config draft + persist/recompute callbacks (Set Target / Facility Inputs dialogs
	// Save via onCalculate). One grouped prop shared with the breakdown table + sidebar.
	draftControls: PbjDraftControls;
	// The persisted row's CMI pair. Compared against the draft to tell "CASPER auto-populated this"
	// apart from "the admin typed it", which decides whether the CMI chip/dialog shows CASPER values.
	savedCmi: SavedCmi;
	// The lifted Action-Plan outcome (projected score, cost, reaches-target, headline hours),
	// computed once by ScoringAndRecommendations from the shared `selectedPlan`. The hero
	// renders the score lead's projection + the verdict line + the progress-bar projected
	// extension from these — so it always agrees with the "How" column's plan to the point.
	// `reachesTarget` already folds in the buffered target, so the hero never re-derives it.
	planOutcome: PlanOutcome;
	// True while the Action Plan is exactly the optimizer's suggestion — the savings line then says
	// "Suggested plan saves…"; a plan the user built or changed reads "Your plan saves…".
	planIsSuggested: boolean;
	// Committed range (ISO) shown as the prominent period in the hero's orientation header.
	periodStart: string;
	periodEnd: string;
	// Wraps the hero's "Change period" affordance in the page-level period/facility popover
	// (the controls + Calculate live at the page; the popover anchors to this trigger). A
	// render-prop so the popover stays child-anchored while the hero styles its own trigger.
	renderChangePeriod: (trigger: ReactElement) => ReactNode;
};

/**
 * The Prediction tab's hero band — a compact, deliberately-filled orientation stage (no
 * longer a tall gauge stage, and no longer a hollow 3-column grid). Layout:
 *   HEADER TOOLBAR — orientation on the left (facility + state + Census/CMI + period) and the
 *                    "Change period & facility" affordance on the right, so the bar reads as
 *                    orientation-left / change-period-right and stays balanced.
 *   BODY (3 zones) — the score lead (stars + points) | a hairline | the gap story (large
 *                    narrative + verdict) centerpiece | a hairline | a right action column
 *                    (Set Target, centered in the row) that fills what would otherwise be an
 *                    empty right edge. Manual Hours moved out to the Scoring Breakdown card
 *                    (DAT-608), where the numbers it moves actually live.
 *   FULL-WIDTH METER — the now→projected→target progress bar spans the whole band beneath the
 *                    zones, anchoring the horizontal width.
 * On a narrow container the zones stack, centered (score lead → gap story → full-width
 * actions). Owns the Set Target + Census + Facility Inputs (CMI) dialogs since those are its
 * actions — the two orientation chips open SEPARATE menus: Census → the census-only
 * `CensusDialog`, CMI → `FacilityInputsDialog` (CMI fraction + national HRDs + safety margin).
 * All values come from the live prediction/draft.
 */
export const PredictionHero: FC<Props> = ({
	prediction,
	draftControls,
	savedCmi,
	planOutcome,
	planIsSuggested,
	periodStart,
	periodEnd,
	renderChangePeriod,
}) => {
	const { draft, onDraftChange, onCalculate } = draftControls;
	const { t } = useTranslation();
	const theme = useTheme();
	const [setTargetOpen, setSetTargetOpen] = useState(false);
	const [cmiOpen, setCmiOpen] = useState(false);
	const [censusOpen, setCensusOpen] = useState(false);

	// Translucent-white ghost sx for the dark hero's body action (Set Target). Kept here (not in
	// SystemButton) since this "on a dark surface" treatment is local to the hero stage.
	// `width: 100%` makes the button fill the action column rather than float in it.
	const actionGhostSx = {
		width: { xs: "100%", sm: "100%" },
		backgroundColor: heroGhostFill(),
		color: heroInk(),
		border: `1px solid ${heroGhostBorder()}`,
		fontWeight: 600,
		"&:hover": { backgroundColor: heroGhostHover(), border: `1px solid ${heroGhostBorder()}` },
	} as const;

	// Period label — a compact "Jan 1 – Mar 31, 2026" from the committed ISO range.
	const periodLabel = useMemo(() => {
		const s = periodStart ? DateTime.fromISO(periodStart) : null;
		const e = periodEnd ? DateTime.fromISO(periodEnd) : null;
		if (!s?.isValid || !e?.isValid) return null;
		const sameYear = s.year === e.year;
		const startFmt = sameYear ? { month: "short", day: "numeric" } : DateTime.DATE_MED;
		return `${s.toLocaleString(startFmt as Intl.DateTimeFormatOptions)} – ${e.toLocaleString(DateTime.DATE_MED)}`;
	}, [periodStart, periodEnd]);

	// Resolve the target through the single source of truth (shared with ScoringAndRecommendations)
	// so the progress meter, the gap callout, and the verdict all measure against the SAME buffered
	// goal the plan-outcome `reachesTarget` uses. Reads the live draft so editing + saving the rating
	// updates the label/gap/meter immediately. `targetPoints` is BUFFERED (base + safety margin);
	// `baseTargetPoints` is the un-buffered goal the Set Target dialog edits.
	const { targetStars, targetPoints, baseTargetPoints, bufferPoints } = useMemo(() => resolveTargetPoints(prediction, draft), [prediction, draft]);
	const meetsTarget = prediction.currentStars >= targetStars;

	// The CMI the SCORER used — CASPER's pair when it auto-populated and the admin hasn't edited
	// since load, otherwise the draft's. Keeps the chip, the dialog fields and the score in agreement.
	const casperCmi = useMemo(() => resolveCasperCmi(prediction, draft, savedCmi), [prediction, draft, savedCmi]);
	const cmi = casperCmi.cmi;

	// The chip + census dialog read the pending draft override the moment it's edited, so the
	// value doesn't lag behind until the next Calculate roundtrip (same rule the breakdown
	// table's census chip follows). When there's no override, the scorer's resolved value wins.
	const displayedCensus = draft.averageCensus ?? prediction.resolvedAverageCensus;
	// Only explain the number when it ISN'T a user override — an auto-computed or fallback
	// census is otherwise an unexplained default sitting in an editable field.
	const censusSourceHint =
		prediction.averageCensusSource === "computed"
			? t("pbj_model.config.average_census.computed_hint", { value: prediction.resolvedAverageCensus })
			: prediction.averageCensusSource === "fallback"
				? t("pbj_model.config.average_census.fallback_hint", { value: prediction.resolvedAverageCensus })
				: undefined;

	const pointsGap = useMemo(() => Math.max(0, Math.round(targetPoints - prediction.totalPoints)), [prediction.totalPoints, targetPoints]);
	const roundedTarget = Math.round(targetPoints);

	// Plan-outcome projections lifted from the parent. The hero renders the score lead's
	// projection, the verdict line, and the progress-bar projected extension from these — so
	// they always agree with the "How" column's Action Plan. Dragging a lever / Suggest-plan
	// recomputes `planOutcome` upstream and re-renders the hero live.
	const currentPointsRounded = Math.round(prediction.totalPoints);
	const withPlanPoints = Math.round(planOutcome.projectedPoints);
	// Only treat the plan as an active projection when it adds meaningful points beyond the current score.
	// Drives the score lead's "→ with plan" and the meter's projected extension, which draw a LIFT.
	const hasActivePlan = planOutcome.hasPlan && planOutcome.projectedPoints > prediction.totalPoints + MIN_PLAN_LIFT_POINTS;
	// A plan with cuts is a real plan even when it lifts nothing (a cut-only or net-zero swap plan), so the
	// gap story states it instead of prompting the user to "suggest a plan" they already have.
	const planHasCuts = planOutcome.totalSavings > 0 || planOutcome.cutWeeklyHours > 0;
	const planIsShown = hasActivePlan || (planOutcome.hasPlan && planHasCuts);
	// How far the plan still is from the (rounded) star threshold, for the "still N short" copy.
	const withPlanGap = Math.max(0, roundedTarget - withPlanPoints);
	// Net PPD as a magnitude; `planIsNetSavings` tells the copy whether it's spend or savings.
	const planPpdLabel = formatCurrency(Math.round(Math.abs(planOutcome.totalPpd)), 0);
	const planIsNetSavings = planOutcome.totalPrice < 0;
	// Added and cut hours stay apart: a swap (cut RN, add CNA) nets to ~0 and would read "+0 hrs/wk".
	const planAddedHours = Math.round(planOutcome.addedWeeklyHours);
	const planCutHours = Math.round(planOutcome.cutWeeklyHours);
	// Decision H: a department already at target sees the plan's quarter savings — otherwise a
	// savings-only plan (which lowers points, so never counts as an "active" lift) is invisible here.
	const planSavesLabel =
		meetsTarget && planOutcome.hasPlan && planOutcome.reachesTarget && planIsNetSavings ? formatCurrency(Math.round(-planOutcome.totalPrice), 0) : null;
	// At target, a plan whose cuts give up more than the margin above the goal drops the department
	// below it — say so, in points, rather than showing "You've reached your target" over a plan that
	// undoes it. `remainingPoints` is ceil-rounded, so it is at least 1 whenever the target is missed.
	const planDropsBelowBy = meetsTarget && planOutcome.hasPlan && planHasCuts && !planOutcome.reachesTarget ? planOutcome.remainingPoints : null;
	// Does the plan clear the (buffered) target? Drives the success vs "still short" verdict.
	const planReachesTarget = planOutcome.reachesTarget;

	// Raw points across the still-available measures — the numerator of the CMS rescale
	// (raw × maxPoints / availableMaxPoints). Only surfaced when `rescaled`.
	const availablePoints = useMemo(() => prediction.categories.filter((c) => c.available).reduce((sum, c) => sum + c.score, 0), [prediction.categories]);

	const handleSetTargetSave = (nextPoints: number, nextStars: number): void => {
		onCalculate({ ...draft, targetPoints: nextPoints, stars: nextStars });
		setSetTargetOpen(false);
	};

	// A quiet glass chip for the orientation header. Optional onClick makes it a button (Census/CMI).
	const orientationChip = (label: ReactNode, opts: { icon: ReactNode; onClick?: () => void; dataTestId?: string }): ReactNode => (
		<Chip
			icon={opts.icon as React.ReactElement}
			label={label}
			size="small"
			onClick={opts.onClick}
			sx={{
				height: 28,
				bgcolor: heroGlassPill(),
				color: heroInk(),
				border: `1px solid ${heroLine()}`,
				fontWeight: 500,
				fontSize: "0.75rem",
				cursor: opts.onClick ? "pointer" : "default",
				"& .MuiChip-icon": { color: heroInkMuted(), fontSize: 15 },
				"&:hover": opts.onClick ? { bgcolor: heroGhostHover() } : undefined,
			}}
			data-testid={opts.dataTestId}
		/>
	);

	// Shared by the two period affordances (the date range + "Period & facility") — both open the
	// same popover, so both wear the same treatment.
	const periodTriggerSx = {
		display: "inline-flex",
		alignItems: "center",
		gap: 0.625,
		cursor: "pointer",
		fontSize: FONT_SIZE.LABEL_SM,
		fontWeight: 700,
		color: heroInk(),
		px: 1,
		py: 0.75,
		borderRadius: PBJ_RADIUS.CONTROL,
		border: `1px solid ${heroGhostBorder()}`,
		bgcolor: heroGhostFill(),
		transition: "background-color 120ms",
		"&:hover": { bgcolor: heroGhostHover() },
		"@media (prefers-reduced-motion: reduce)": { transition: "none" },
	} as const;

	return (
		<>
			<Paper
				elevation={0}
				sx={{
					position: "relative",
					overflow: "hidden",
					p: { xs: 2, md: 2.25 },
					borderRadius: PBJ_RADIUS.CARD,
					// The dark premium stage — a darkened-primary gradient (same family as the prod
					// header) with a brand-light glow + faint gold warmth. Depth via a float shadow.
					background: heroSurface(theme),
					boxShadow: floatShadow(theme),
				}}
				data-testid="pbj-model-hero"
			>
				{/* HEADER TOOLBAR — orientation on the left (facility + state + Census/CMI + period)
				    and the "Change period & facility" affordance on the right. Balanced as
				    orientation-left / change-period-right; the Set Target + Manual Hours actions
				    now live in the body's right action column so this bar stays a clean two-ender. */}
				<Stack
					direction="row"
					sx={{ alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1.75, pb: 1.5, borderBottom: `1px solid ${heroLine()}` }}
					data-testid="pbj-model-hero-context"
				>
					<Stack direction="row" spacing={1.25} sx={{ alignItems: "center", minWidth: 0, flexWrap: "wrap", rowGap: 1 }}>
						{draft.facilityId && (
							<Stack direction="row" spacing={0.875} sx={{ alignItems: "center", minWidth: 0 }} data-testid="pbj-model-hero-facility">
								<DomainOutlinedIcon sx={{ fontSize: 20, color: heroInkMuted() }} />
								<Typography sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: heroInk(), whiteSpace: "nowrap" }}>
									{t("pbj_model.hero.facility_pill", { id: draft.facilityId })}
								</Typography>
								{draft.stateCode && (
									<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 500, color: heroInkMuted(), whiteSpace: "nowrap" }}>
										{t("pbj_model.hero.state_pill", { code: draft.stateCode })}
									</Typography>
								)}
							</Stack>
						)}
						{orientationChip(t("pbj_model.stars.census.chip", { value: displayedCensus }), {
							icon: <GroupsOutlinedIcon />,
							onClick: () => setCensusOpen(true),
							dataTestId: "pbj-model-hero-census-chip",
						})}
						{orientationChip(t("pbj_model.stars.cmi.chip", { value: cmi.toFixed(2) }), {
							icon: <CalculateIcon />,
							onClick: () => setCmiOpen(true),
							dataTestId: "pbj-model-cmi-chip",
						})}
						{periodLabel &&
							renderChangePeriod(
								<Box sx={periodTriggerSx} data-testid="pbj-model-hero-period-pill">
									<CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />
									{periodLabel}
								</Box>,
							)}
					</Stack>
					<Stack
						direction="row"
						spacing={1}
						sx={{ alignItems: "center", ml: { xs: 0, sm: "auto" }, flexWrap: "wrap", rowGap: 1 }}
						data-testid="pbj-model-hero-actions"
					>
						{renderChangePeriod(
							<Box data-testid="pbj-model-hero-change-period" sx={periodTriggerSx}>
								<TuneIcon sx={{ fontSize: 15 }} />
								{t("pbj_model.hero.change_period")}
							</Box>,
						)}
					</Stack>
				</Stack>

				{/* BODY — three zones read as ONE instrument: the enlarged score block, a vertical
				    hairline, the gap story centerpiece, a second hairline, then the right action
				    column. Zones are stretched (not centered) so each fills the row height beside
				    the taller verdict — no floating void — and the hairlines run the full height as
				    intentional separation. The action column fills what used to read as empty. */}
				<Box
					sx={{
						display: "grid",
						alignItems: "stretch",
						gap: 3,
						// Default (narrow container / mobile): the zones stack, centered — score lead
						// → gap story → actions. At the multi-column threshold: score lead (auto) |
						// hairline | gap story (fluid) | hairline | actions (auto).
						gridTemplateColumns: "1fr",
						justifyItems: "center",
						textAlign: "center",
						[PREDICTION_MULTICOL_QUERY]: {
							gridTemplateColumns: "auto minmax(0, 1fr) auto",
							gap: 0,
							justifyItems: "stretch",
							textAlign: "left",
						},
					}}
				>
					{/* LEFT — the score lead (enlarged stars as the focal headline + points). The
					    hairline + right padding live here so the two zones read as one instrument
					    instead of two islands separated by a 40px void. */}
					<Box
						sx={{
							justifySelf: "center",
							display: "flex",
							[PREDICTION_MULTICOL_QUERY]: {
								justifySelf: "stretch",
								pr: 3,
								borderRight: `1px solid ${heroLine()}`,
							},
						}}
					>
						<HeroScoreLead
							currentStars={prediction.currentStars}
							targetStars={targetStars}
							currentPointsRounded={currentPointsRounded}
							hasActivePlan={hasActivePlan}
							withPlanPoints={withPlanPoints}
							maxPoints={prediction.maxPoints}
						/>
					</Box>

					{/* CENTER — the gap story centerpiece (eyebrow → large narrative → verdict). */}
					<Box sx={{ width: "100%", minWidth: 0, [PREDICTION_MULTICOL_QUERY]: { pl: 3 } }}>
						<HeroGapStory
							meetsTarget={meetsTarget}
							targetStars={targetStars}
							planIsShown={planIsShown}
							hasActivePlan={hasActivePlan}
							planReachesTarget={planReachesTarget}
							planDropsBelowBy={planDropsBelowBy}
							planIsSuggested={planIsSuggested}
							pointsGap={pointsGap}
							withPlanPoints={withPlanPoints}
							withPlanGap={withPlanGap}
							roundedTarget={roundedTarget}
							planAddedHours={planAddedHours}
							planCutHours={planCutHours}
							planPpdLabel={planPpdLabel}
							planIsNetSavings={planIsNetSavings}
							planSavesLabel={planSavesLabel}
						/>
					</Box>

					{/* RIGHT — the action column. Set Target alone since DAT-608 moved Manual Hours
					    next to the Scoring Breakdown it feeds. A leading hairline (mirroring the
					    score→story one) marks it as a deliberate zone. When the hero stacks (narrow
					    container), it drops below the gap story as a full-width button capped to the
					    verdict's width. */}
					<Box
						sx={{
							justifySelf: "center",
							width: "100%",
							maxWidth: { xs: 460, md: "none" },
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "stretch",
							gap: 1,
							[PREDICTION_MULTICOL_QUERY]: {
								justifySelf: "stretch",
								// Fixed group width so the button reads as a tidy, intentional action block
								// rather than a sparse floater; it stretches to fill the column.
								width: 200,
								maxWidth: "none",
								pl: 3,
								borderLeft: `1px solid ${heroLine()}`,
							},
						}}
						data-testid="pbj-model-hero-actions-column"
					>
						<SystemButton
							buttonVariant="secondary"
							size="small"
							leadingIcon={<TrackChangesIcon sx={{ fontSize: 16 }} />}
							onClick={() => setSetTargetOpen(true)}
							sx={actionGhostSx}
							data-testid="pbj-model-set-target-button"
						>
							{t("pbj_model.stars.set_target.button")}
						</SystemButton>
						{/* The goal the plan is actually measured against is the star band's points PLUS the
						    safety buffer, and the two were only ever visible in separate dialogs. Spelling the
						    sum out here is what stops "3★ = 205" reading as the number to beat when the plan
						    is being judged against 215. */}
						<InHouseTooltip arrow title={t("pbj_model.stars.set_target.aim_tooltip")}>
							<Typography
								sx={{ fontSize: FONT_SIZE.LABEL_XS, color: heroInkMuted(), textAlign: "center", cursor: "help" }}
								data-testid="pbj-model-set-target-aim"
							>
								{t(bufferPoints > 0 ? "pbj_model.stars.set_target.aim" : "pbj_model.stars.set_target.aim_no_buffer", {
									count: targetStars,
									base: Math.round(baseTargetPoints),
									buffer: Math.round(bufferPoints),
									aim: roundedTarget,
								})}
							</Typography>
						</InHouseTooltip>
					</Box>
				</Box>

				{/* FULL-WIDTH METER — the now→projected→target bar spans the whole band, anchoring
				    the width so the hero never reads hollow. The bar carries its own labelled axis
				    (no legend row), so it needs a little more air above than when a text row sat
				    between it and the body zones. */}
				<Box sx={{ mt: 2.25 }}>
					<HeroProgressMeter
						prediction={prediction}
						hasActivePlan={hasActivePlan}
						currentPointsRounded={currentPointsRounded}
						withPlanPoints={withPlanPoints}
						roundedTarget={roundedTarget}
						projectedPoints={planOutcome.projectedPoints}
						availablePoints={availablePoints}
					/>
				</Box>
			</Paper>

			{/* The Census chip's own menu — census only. Save persists + recomputes (rather than
			    just staging the draft) so the chip, the HPPD-to-hours math, and the score all
			    move together off one action, matching the hero's other dialogs. */}
			<CensusDialog
				open={censusOpen}
				onClose={() => setCensusOpen(false)}
				initialCensus={displayedCensus}
				sourceHint={censusSourceHint}
				onSave={(nextCensus) => onCalculate({ ...draft, averageCensus: nextCensus })}
			/>

			<FacilityInputsDialog
				open={cmiOpen}
				onClose={() => setCmiOpen(false)}
				draft={draft}
				cmi={cmi}
				casperCmi={casperCmi}
				onDraftChange={onDraftChange}
				onCalculate={onCalculate}
			/>

			<SetTargetDialog
				open={setTargetOpen}
				onClose={() => setSetTargetOpen(false)}
				onSave={handleSetTargetSave}
				initialTargetPoints={Math.round(baseTargetPoints)}
				maxPoints={prediction.maxPoints}
				starThresholds={prediction.starThresholds}
				departmentId={prediction.config.departmentId}
			/>
		</>
	);
};
